import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../../firebaseClient';
import { useProfiles } from 'hooks/useProfiles';
import Icon from 'components/AppIcon';
import GlassyModal from 'components/ui/GlassyModal';

const CommentSearch = ({ isOpen, onClose }) => {
  const { profiles } = useProfiles();
  const [selectedClient, setSelectedClient] = useState('');
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);

  // Get all client profiles
  const clientProfiles = profiles.filter(p => p.is_client);

  useEffect(() => {
    if (!selectedClient) {
      setComments([]);
      setSearchResults([]);
      return;
    }

    setLoading(true);
    
    // Query comments from selected client
    const q = query(
      collection(db, 'comments'),
      where('author_id', '==', selectedClient),
      orderBy('created_at', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const clientComments = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        created_at: doc.data().created_at?.toDate() || new Date()
      }));

      setComments(clientComments);
      setSearchResults(clientComments);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [selectedClient]);

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'text-red-400';
      case 'writer': return 'text-green-400';
      case 'qa': return 'text-blue-400';
      case 'client': return 'text-purple-400';
      default: return 'text-gray-400';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return 'Shield';
      case 'writer': return 'PenTool';
      case 'qa': return 'CheckCircle';
      case 'client': return 'User';
      default: return 'User';
    }
  };

  const filterComments = (searchTerm) => {
    if (!searchTerm.trim()) {
      setSearchResults(comments);
      return;
    }

    const filtered = comments.filter(comment => 
      comment.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      comment.item_type.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    setSearchResults(filtered);
  };

  const selectedClientProfile = clientProfiles.find(p => p.id === selectedClient);

  return (
    <GlassyModal
      isOpen={isOpen}
      onClose={onClose}
      title="Search Client Comments"
      icon="Search"
      size="large"
    >
      <div className="space-y-6">
        {/* Client Selection */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-white">Select Client</label>
          <select
            value={selectedClient}
            onChange={(e) => setSelectedClient(e.target.value)}
            className="w-full p-3 bg-white/10 border border-white/20 rounded-lg text-white focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
          >
            <option value="">Choose a client...</option>
            {clientProfiles.map(client => (
              <option key={client.id} value={client.id} className="bg-gray-800">
                {client.full_name} ({client.email})
              </option>
            ))}
          </select>
        </div>

        {/* Search within comments */}
        {selectedClient && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-white">Search Comments</label>
            <div className="relative">
              <input
                type="text"
                placeholder="Search by content or item type..."
                onChange={(e) => filterComments(e.target.value)}
                className="w-full p-3 pl-10 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
              <Icon name="Search" size={20} className="absolute left-3 top-3 text-white/50" />
            </div>
          </div>
        )}

        {/* Results */}
        {selectedClient && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-white">
                Comments from {selectedClientProfile?.full_name}
              </h3>
              <span className="text-white/60 text-sm">
                {searchResults.length} comment{searchResults.length !== 1 ? 's' : ''} found
              </span>
            </div>

            {loading ? (
              <div className="text-center py-8">
                <Icon name="Loader2" size={32} className="animate-spin text-white/60 mx-auto mb-2" />
                <p className="text-white/60">Loading comments...</p>
              </div>
            ) : searchResults.length === 0 ? (
              <div className="text-center py-8">
                <Icon name="MessageCircle" size={32} className="text-white/20 mx-auto mb-2" />
                <p className="text-white/60">No comments found</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                <AnimatePresence>
                  {searchResults.map((comment) => (
                    <motion.div
                      key={comment.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      className="bg-white/5 rounded-lg p-4 border border-white/10"
                    >
                      <div className="flex items-start space-x-3">
                        <div className="w-8 h-8 bg-primary/20 rounded-full flex items-center justify-center">
                          <Icon 
                            name={getRoleIcon(comment.author_role)} 
                            size={14} 
                            className={getRoleColor(comment.author_role)} 
                          />
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-2">
                            <span className="text-white font-medium text-sm">
                              {comment.author_name}
                            </span>
                            <span className={`text-xs px-2 py-1 rounded-full bg-white/10 ${getRoleColor(comment.author_role)}`}>
                              {comment.author_role}
                            </span>
                            <span className="text-white/60 text-xs">
                              {format(comment.created_at, 'MMM dd, yyyy HH:mm')}
                            </span>
                          </div>
                          
                          <div className="mb-2">
                            <span className="text-white/80 text-xs bg-white/10 px-2 py-1 rounded">
                              {comment.item_type} • {comment.parent_id ? 'Reply' : 'Comment'}
                            </span>
                          </div>
                          
                          <p className="text-white/80 text-sm whitespace-pre-wrap bg-white/5 p-2 rounded">
                            {comment.content}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}
          </div>
        )}
      </div>
    </GlassyModal>
  );
};

export default CommentSearch;